import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/site";
import { LeadMagnet } from "@/components/LeadMagnet";
import { api } from "@/lib/api";

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="page-packages">
      <SEO title="Packages & Pricing" description="TWORAY advisory packages: Foundation £1,200, Business Ready £3,000, Scale-Up Pro £4,500. Excl. VAT, indicative and subject to scope." />
      <PageHero overline="Packages" title="Fixed-scope packages with pricing you can see."
        subtitle="Three ways to start. Prices exclude VAT and are indicative, subject to scope. Prefer a bespoke engagement? Just ask." />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {loading ? (<div className="text-center text-slate">Loading packages…</div>) : (
            <div className="grid gap-6 lg:grid-cols-3 items-start">
              {packages.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <div data-testid={`package-${p.slug}`} className={`relative rounded-md border p-8 h-full flex flex-col ${p.recommended ? "border-brandblue bg-navy text-white shadow-2xl lg:-mt-4 lg:mb-4" : "border-brandborder bg-white"}`}>
                    {p.recommended && (<span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-cyan text-navy text-xs font-bold px-3 py-1 rounded-sm"><Star size={12} /> Recommended</span>)}
                    <h3 className={`font-heading font-bold text-2xl ${p.recommended ? "text-white" : "text-navy"}`}>{p.name}</h3>
                    <p className={`mt-1 text-sm ${p.recommended ? "text-white/70" : "text-slate"}`}>{p.tagline}</p>
                    <div className="mt-6">
                      <span className={`font-heading font-extrabold text-5xl tracking-tight ${p.recommended ? "text-cyan" : "text-navy"}`}>{p.price}</span>
                      <p className={`mt-2 text-xs ${p.recommended ? "text-white/50" : "text-slate"}`}>{p.price_note}</p>
                      {p.value_note ? (<p className={`mt-1 text-xs font-semibold ${p.recommended ? "text-cyan" : "text-brandblue"}`}>{p.value_note}</p>) : null}
                    </div>
                    <ul className="mt-8 space-y-3 flex-1">
                      {p.features.map((f) => {
                        const heading = f.toLowerCase().startsWith("everything in");
                        return heading ? (
                          <li key={f} className={`text-sm font-semibold ${p.recommended ? "text-white/90" : "text-navy"}`}>{f}</li>
                        ) : (
                          <li key={f} className="flex items-start gap-2.5">
                            <Check size={18} className={`mt-0.5 shrink-0 ${p.recommended ? "text-cyan" : "text-brandblue"}`} />
                            <span className={`text-sm ${p.recommended ? "text-white/85" : "text-navy"}`}>{f}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <Link to={`/book?package=${p.slug}`} data-testid={`package-cta-${p.slug}`} className={`mt-8 inline-flex items-center justify-center font-semibold text-sm px-6 py-3 rounded-sm transition-colors duration-300 ${p.recommended ? "bg-cyan text-navy hover:bg-white" : "bg-navy text-white hover:bg-brandblue"}`}>{p.cta || "Get started"}</Link>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
          <Reveal>
            <p className="mt-12 text-center text-sm text-slate max-w-2xl mx-auto">
              All prices exclude VAT and are indicative, subject to scope. See individual{" "}
              <Link to="/individual-services" className="text-brandblue font-semibold link-underline pb-0.5">services &amp; pricing</Link>. A full-price
              brochure is available after you book a discovery call.
            </p>
          </Reveal>

          <Reveal>
            <div className="mt-16 max-w-5xl mx-auto">
              <LeadMagnet variant="section" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
